import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useSearchParams, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import DemoTour from "@/components/DemoTour";
import UpgradeModal from "@/components/UpgradeModal";

// Public Pages
import Home from "@/pages/public/Home";
import PublicRegistry from "@/pages/public/PublicRegistry";
import Pricing from "@/pages/public/Pricing";
import CertificateVerify from "@/pages/public/CertificateVerify";
import CreditRegistryPage from "@/pages/public/CreditRegistryPage";
import PackVerify from "@/pages/public/PackVerify";
import GetStarted from "@/pages/public/GetStarted";
import StateEntry from "@/pages/app/states/StateEntry";
import StateLayout from "@/pages/app/states/StateLayout";
import ProjectStart from "@/pages/app/project/ProjectStart";
import AdminWorkspace from "@/pages/admin/AdminWorkspace";
import AnalystReviewConsole from "@/pages/admin/AnalystReviewConsole";
import PlatformIntegrityDashboard from "@/pages/app/admin/PlatformIntegrityDashboard";

// Dangote Corporate Platform
import DangoteDashboard      from "@/pages/app/dangote/DangoteDashboard";
import DangoteBusinessUnit   from "@/pages/app/dangote/DangoteBusinessUnit";
import DangoteMonthlyReport  from "@/pages/app/dangote/DangoteMonthlyReport";
import DangoteAssetDetail    from "@/pages/app/dangote/DangoteAssetDetail";
import DangoteCarbonStrategy from "@/pages/app/dangote/DangoteCarbonStrategy";
import DangoteFinancing      from "@/pages/app/dangote/DangoteFinancing";
import DangoteReports        from "@/pages/app/dangote/DangoteReports";
import DangoteSDGContribution from "@/pages/app/dangote/DangoteSDGContribution";
import DangoteEvidenceVault    from "@/pages/app/dangote/DangoteEvidenceVault";
import DangoteCarbonStructuring from "@/pages/app/dangote/DangoteCarbonStructuring";
import DangoteCarbonValueBridge from "@/pages/app/dangote/DangoteCarbonValueBridge";
import DangoteClimateSnapshot from "@/pages/app/dangote/DangoteClimateSnapshot";
import { DangoteProvider } from "@/context/DangoteContext";
import { NBPLCProvider } from "@/context/NBPLCContext";
import { NIRSALProvider } from "@/context/NIRSALContext";
import { LAGOSProvider } from "@/context/LAGOSContext";
// LCFPI — Lagos Climate Finance Pipeline Initiative
import { LCFPIProvider } from "@/context/LCFPIContext";
import LCFPILayout         from "@/pages/app/lcfpi/LCFPILayout";
import LCFPIDashboard      from "@/pages/app/lcfpi/LCFPIDashboard";
import LCFPIPipeline       from "@/pages/app/lcfpi/LCFPIPipeline";
// Six-tab container pages (new executive structure)
import LCFPIProjectsTab    from "@/pages/app/lcfpi/LCFPIProjectsTab";
import LCFPIFinanceTab     from "@/pages/app/lcfpi/LCFPIFinanceTab";
import LCFPIImpactTab      from "@/pages/app/lcfpi/LCFPIImpactTab";
import LCFPIGovernanceTab  from "@/pages/app/lcfpi/LCFPIGovernanceTab";
// IC / Presentation Mode — kept as standalone full-screen
import LCFPIInvestmentCommitteeMode from "@/pages/app/lcfpi/LCFPIInvestmentCommitteeMode";
import LCFPILanding        from "@/pages/public/LCFPILanding";
// DBN Pages
import DBNLayout                from "@/pages/app/dbn/DBNLayout";
import DBNOverview              from "@/pages/app/dbn/DBNOverview";
import DBNEIBFacility           from "@/pages/app/dbn/DBNEIBFacility";
import DBNPipeline              from "@/pages/app/dbn/DBNPipeline";
import DBNProjects              from "@/pages/app/dbn/DBNProjects";
import DBNRiskAdditionality     from "@/pages/app/dbn/DBNRiskAdditionality";
import DBNMonetisation          from "@/pages/app/dbn/DBNMonetisation";
import DBNReporting             from "@/pages/app/dbn/DBNReporting";
import DBNAICopilot             from "@/pages/app/dbn/DBNAICopilot";
import DBNEligibilityAssessment from "@/pages/app/dbn/DBNEligibilityAssessment";
import DBNTAManager             from "@/pages/app/dbn/DBNTAManager";
// MTN Nigeria Pages
import { MTNProvider } from "@/context/MTNContext";
import MTNLayout           from "@/pages/app/mtn/MTNLayout";
import MTNDashboard        from "@/pages/app/mtn/MTNDashboard";
import MTNWorkspace        from "@/pages/app/mtn/MTNWorkspace";
import MTNIntelligence     from "@/pages/app/mtn/MTNIntelligence";
import MTNSdgImpact        from "@/pages/app/mtn/MTNSdgImpact";
import MTNFinance          from "@/pages/app/mtn/MTNFinance";
import MTNCarbonMarkets    from "@/pages/app/mtn/MTNCarbonMarkets";
import MTNPddReadiness     from "@/pages/app/mtn/MTNPddReadiness";
import MTNEsgDisclosure    from "@/pages/app/mtn/MTNEsgDisclosure";
import MTNEvidenceVault    from "@/pages/app/mtn/MTNEvidenceVault";
import MTNSettings         from "@/pages/app/mtn/MTNSettings";
// Nosak Group Pages
import { NOSAKProvider } from "@/context/NOSAKContext";
import NosAKDashboard        from "@/pages/app/nosak/NosAKDashboard";
import NosAKDataWorkspace    from "@/pages/app/nosak/NosAKDataWorkspace";
import NosAKIntelligence     from "@/pages/app/nosak/NosAKIntelligence";
import NosAKFinanceCarbon    from "@/pages/app/nosak/NosAKFinanceCarbon";
import NosAKESGRisk          from "@/pages/app/nosak/NosAKESGRisk";
import NosAKEvidenceVault    from "@/pages/app/nosak/NosAKEvidenceVault";
import NosAKSettings         from "@/pages/app/nosak/NosAKSettings";
import NosAKLayout from "@/pages/app/nosak/NosAKLayout";
// Lagos State Pages
import LAGOSDashboard         from "@/pages/app/lagos/LAGOSDashboard";
import LAGOSEntityReporting   from "@/pages/app/lagos/LAGOSEntityReporting";
import LAGOSEntityProfile     from "@/pages/app/lagos/LAGOSEntityProfile";
import LAGOSClimateCircularity from "@/pages/app/lagos/LAGOSClimateCircularity";
import LAGOSSDGContribution   from "@/pages/app/lagos/LAGOSSDGContribution";
import LAGOSCarbonValueBridge from "@/pages/app/lagos/LAGOSCarbonValueBridge";
import LAGOSCarbonStrategy    from "@/pages/app/lagos/LAGOSCarbonStrategy";
import LAGOSCarbonStructuring from "@/pages/app/lagos/LAGOSCarbonStructuring";
import LAGOSFinancials        from "@/pages/app/lagos/LAGOSFinancials";
import LAGOSEvidenceVault     from "@/pages/app/lagos/LAGOSEvidenceVault";
import LAGOSReports           from "@/pages/app/lagos/LAGOSReports";
import LAGOSStateIntelligence from "@/pages/app/lagos/LAGOSStateIntelligence";
import LAGOSEcoLabels         from "@/pages/app/lagos/LAGOSEcoLabels";
import LAGOSSettings          from "@/pages/app/lagos/LAGOSSettings";
import LAGOSClimateInvestmentStructuring from "@/pages/app/lagos/LAGOSClimateInvestmentStructuring";

// NB Pages
import NBPLCDashboard        from "@/pages/app/nbplc/NBPLCDashboard";
import NBPLCMonthlyReport    from "@/pages/app/nbplc/NBPLCMonthlyReport";
import NBPLCClimateSnapshot  from "@/pages/app/nbplc/NBPLCClimateSnapshot";
import NBPLCSDGContribution  from "@/pages/app/nbplc/NBPLCSDGContribution";
import NBPLCCarbonValueBridge from "@/pages/app/nbplc/NBPLCCarbonValueBridge";
import NBPLCCarbonStrategy   from "@/pages/app/nbplc/NBPLCCarbonStrategy";
import NBPLCCarbonStructuring from "@/pages/app/nbplc/NBPLCCarbonStructuring";
import NBPLCEvidenceVault    from "@/pages/app/nbplc/NBPLCEvidenceVault";
import NBPLCFinancing        from "@/pages/app/nbplc/NBPLCFinancing";
import NBPLCReports          from "@/pages/app/nbplc/NBPLCReports";
// NIRSAL Pages
import NIRSALDashboard        from "@/pages/app/nirsal/NIRSALDashboard";
import NIRSALPortfolioReport  from "@/pages/app/nirsal/NIRSALPortfolioReport";
import NIRSALClimateImpact    from "@/pages/app/nirsal/NIRSALClimateImpact";
import NIRSALSDGContribution  from "@/pages/app/nirsal/NIRSALSDGContribution";
import NIRSALCarbonValueBridge from "@/pages/app/nirsal/NIRSALCarbonValueBridge";
import NIRSALCarbonStrategy   from "@/pages/app/nirsal/NIRSALCarbonStrategy";
import NIRSALCarbonStructuring from "@/pages/app/nirsal/NIRSALCarbonStructuring";
import NIRSALEvidenceVault    from "@/pages/app/nirsal/NIRSALEvidenceVault";
import NIRSALFinancials       from "@/pages/app/nirsal/NIRSALFinancials";
import NIRSALReports          from "@/pages/app/nirsal/NIRSALReports";
import AdminVerifiers from "@/pages/admin/AdminVerifiers";
import VerifierPortal from "@/pages/verifier/VerifierPortal";
import CAIControlTower from "@/pages/app/cai/CAIControlTower";
import CAIProjectWorkspace from "@/pages/app/cai/CAIProjectWorkspace";
import UNFCCCReportingPage from "@/pages/app/cai/UNFCCCReportingPage";
import UpgradePage from "@/pages/app/UpgradePage";
import SMEJourneyPage from "@/pages/app/SMEJourneyPage";
import SMEReports from "@/pages/app/SMEReports";
import AggregationEnrollment from "@/pages/app/AggregationEnrollment";
import CarbonPathwaysPage from "@/pages/app/CarbonPathwaysPage";
import VerifyReport from "@/pages/public/VerifyReport";
import ProjectVerify from "@/pages/public/ProjectVerify";
import FinancingPage from "@/pages/app/FinancingPage";
import SMEJourneyWorkspace from "@/pages/app/sme/SMEJourneyWorkspace";
import SMEMonthlyInputs from "@/pages/app/sme/SMEMonthlyInputs";
import SMEEvolutionView from "@/pages/app/sme/SMEEvolutionView";
import ProjectAssessmentPage from "@/pages/app/project/ProjectAssessmentPage";
import CoopHousingWorkspace from "@/pages/app/project/CoopHousingWorkspace";
import HousingClimateSnapshot from "@/pages/app/project/HousingClimateSnapshot";
import HousingSDGPage from "@/pages/app/project/HousingSDGPage";
import HousingReports from "@/pages/app/project/HousingReports";
import HousingFinancing from "@/pages/app/project/HousingFinancing";
import HousingCarbonStrategy from "@/pages/app/project/HousingCarbonStrategy";
import CarbonStructuringWorkspace from "@/pages/app/project/CarbonStructuringWorkspace";
import HousingInputData from "@/pages/app/project/HousingInputData";
import HousingEvidenceVault from "@/pages/app/project/HousingEvidenceVault";
import HousingTraining from "@/pages/app/project/HousingTraining";
// ── Generic multi-sector workspace ──────────────────────────────────────────
import GenericProjectWorkspace from "@/pages/app/project/GenericProjectWorkspace";
import GenericInputData from "@/pages/app/project/GenericInputData";
import GenericEvidenceVault from "@/pages/app/project/GenericEvidenceVault";
import GenericCarbonStrategy from "@/pages/app/project/GenericCarbonStrategy";
import GenericFinancing from "@/pages/app/project/GenericFinancing";
import GenericClimateSnapshot from "@/pages/app/project/GenericClimateSnapshot";
import GenericSDGContribution from "@/pages/app/project/GenericSDGContribution";
import GenericCarbonStructuring from "@/pages/app/project/GenericCarbonStructuring";
import GenericReports from "@/pages/app/project/GenericReports";
import GenericTraining from "@/pages/app/project/GenericTraining";
import InvestorDealView from "@/pages/app/investor/InvestorDealView";
import WorkspaceGuard from "@/components/WorkspaceGuard";

// Auth Pages
import Login from "@/pages/auth/Login";
import Signup from "@/pages/auth/Signup";
import PendingApproval from "@/pages/auth/PendingApproval";
import SetPassword from "@/pages/auth/SetPassword";

// App Pages
import Layout from "@/components/Layout";
import Dashboard from "@/pages/app/Dashboard";
import Assets from "@/pages/app/Assets";
import Assessments from "@/pages/app/Assessments";
import AssessmentWorkspace from "@/pages/app/AssessmentWorkspace";
import EvidenceVault from "@/pages/app/EvidenceVault";
import Reports from "@/pages/app/Reports";
import Passport from "@/pages/app/Passport";
import Registry from "@/pages/app/Registry";
import Academy from "@/pages/app/Academy";
import Methodologies from "@/pages/app/Methodologies";
import Settings from "@/pages/app/Settings";
import Onboarding from "@/pages/onboarding/Onboarding";
import AdminConsole from "@/pages/admin/AdminConsole";
import AdminGovernance from "@/pages/admin/AdminGovernance";
import EnterpriseCRM from "@/pages/admin/EnterpriseCRM";
import PendingRegistrations from "@/pages/admin/PendingRegistrations";
import AnalystWorkspace from "@/pages/analyst/AnalystWorkspace";
import FIIncomingRequests from "@/pages/app/fi/FIIncomingRequests";
import FIWorkspace from "@/pages/app/fi/FIWorkspace";
import FIPortfolios from "@/pages/app/fi/FIPortfolios";
import FIPortfolioDashboard from "@/pages/app/fi/FIPortfolioDashboard";
import FIProjects from "@/pages/app/fi/FIProjects";
import FIFinancingReviews from "@/pages/app/fi/FIFinancingReviews";
import ActionsPage from "@/pages/app/ActionsPage";
import DealsPage from "@/pages/app/DealsPage";
import Marketplace from "@/pages/app/Marketplace";
import NigeriaCalculator from "@/pages/app/NigeriaCalculator";
import CorporateDashboard from "@/pages/app/corporate/CorporateDashboard";
import CorporateStructure from "@/pages/app/corporate/CorporateStructure";
import CorporateTargets from "@/pages/app/corporate/CorporateTargets";
import CorporateInitiatives from "@/pages/app/corporate/CorporateInitiatives";
import CorporatePipeline from "@/pages/app/corporate/CorporatePipeline";
import CorporateWorkspace from "@/pages/app/corporate/CorporateWorkspace";
import CorporateProgramBuilder from "@/pages/app/corporate/CorporateProgramBuilder";
import CorporateMonthlyInputs from "@/pages/app/corporate/CorporateMonthlyInputs";
import CorporateEvolutionView from "@/pages/app/corporate/CorporateEvolutionView";
import CorporateCredentials from "@/pages/app/corporate/CorporateCredentials";
import CorporateSDG from "@/pages/app/corporate/CorporateSDG";
import CorporateCarbonMonetisation from "@/pages/app/corporate/CorporateCarbonMonetisation";
import CorporateCarbonStructuring from "@/pages/app/corporate/CorporateCarbonStructuring";
import CorporateFinancingReadiness from "@/pages/app/corporate/CorporateFinancingReadiness";

// SME new pages
import SMEOverview from "@/pages/app/sme/SMEOverview";
import SMECredentials from "@/pages/app/sme/SMECredentials";
import SMEClimateSnapshot from "@/pages/app/sme/SMEClimateSnapshot";
import SMESDGContribution from "@/pages/app/sme/SMESDGContribution";
import SMEFinancingReadiness from "@/pages/app/sme/SMEFinancingReadiness";

// FI new pages
import FIEligibilityGate from "@/pages/app/fi/FIEligibilityGate";
import FICapitalDeployment from "@/pages/app/fi/FICapitalDeployment";

// States workspace
import StateOverview from "@/pages/app/states/StateOverview";
import StateClimateSnapshot from "@/pages/app/states/StateClimateSnapshot";
import StateAggregatedProjects from "@/pages/app/states/StateAggregatedProjects";
import StateCredentials from "@/pages/app/states/StateCredentials";
import StateSDG from "@/pages/app/states/StateSDG";
import StateCarbonPathways from "@/pages/app/states/StateCarbonPathways";
import StateProgrammes from "@/pages/app/states/StateProgrammes";
import StateLabeling from "@/pages/app/states/StateLabeling";
import StateReports from "@/pages/app/states/StateReports";
import StateFinancing from "@/pages/app/states/StateFinancing";

import "@/App.css";
import { JourneyProvider } from "@/context/JourneyContext";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-[#059669] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
        <p className="text-sm text-slate-500">Loading DeCarbonIQ...</p>
      </div>
    </div>
  );
  // Brief post-login window: token exists but React state not yet hydrated.
  // Show spinner instead of bouncing back to /login — prevents login flicker.
  if (!user && localStorage.getItem("decarboniq_token")) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-[#059669] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
        <p className="text-sm text-slate-500">Restoring session...</p>
      </div>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  // Admin primary guard: Dashboard.js redirects admin → /admin on load.
  // ProtectedRoute does NOT redirect admin from workspace routes —
  // admin may intentionally navigate to workspaces for governance oversight.
  return children;
};

const AdminRoute = ({ children }) => {
  const { user } = useAuth();
  if (!user || user.role !== "admin") return <Navigate to="/login" replace />;
  return children;
};

const PublicOnlyRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="w-6 h-6 border-2 border-[#059669] border-t-transparent rounded-full animate-spin mx-auto"></div>
    </div>
  );
  if (user) {
    // Use backend-provided landing route to avoid flash to /dashboard then redirect
    const dest = sessionStorage.getItem("decarboniq_signup_dest");
    if (dest) {
      sessionStorage.removeItem("decarboniq_signup_dest");
      return <Navigate to={dest} replace />;
    }
    // Respect user's actual workspace landing route (not generic /dashboard)
    const routing = user.routing || {};
    const landingRoute =
      routing.landing_route ||
      user.default_workspace_path ||
      "/dashboard";
    return <Navigate to={landingRoute} replace />;
  }
  return children;
};

function AppRoutes() {
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeData,  setUpgradeData]  = useState(null);

  useEffect(() => {
    const handler = (e) => {
      setUpgradeData(e.detail);
      setShowUpgrade(true);
    };
    window.addEventListener("subscription:required", handler);
    return () => window.removeEventListener("subscription:required", handler);
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <DemoTourWrapper>
        {showUpgrade && (
          <UpgradeModal onClose={() => setShowUpgrade(false)} />
        )}
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/get-started" element={<GetStarted />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/verify/pack/:packId" element={<PackVerify />} />
          <Route path="/verify/certificate/:certId" element={<CertificateVerify />} />
          <Route path="/verify/report/:reportId" element={<VerifyReport />} />
          <Route path="/verify/project/:token"   element={<ProjectVerify />} />

          <Route path="/credit/:creditId" element={<CreditRegistryPage />} />
          <Route path="/verify/:registryId" element={<PublicRegistry />} />
          <Route path="/registry" element={<PublicRegistry />} />
          {/* Investor portal — no auth required, token-based */}
          <Route path="/investor/deal/:token" element={<InvestorDealView />} />

          {/* Auth Routes */}
          <Route path="/login" element={<PublicOnlyRoute><Login /></PublicOnlyRoute>} />
          <Route path="/signup" element={<PublicOnlyRoute><Signup /></PublicOnlyRoute>} />
          <Route path="/pending-approval" element={<PendingApproval />} />
          <Route path="/set-password"     element={<SetPassword />} />

          {/* Onboarding */}
          <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />

          {/* Authenticated App Routes */}
          <Route path="/" element={<ProtectedRoute><JourneyProvider><Layout /></JourneyProvider></ProtectedRoute>}>
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="assets" element={<Assets />} />
            <Route path="assessments" element={<Assessments />} />
            <Route path="assessments/:id" element={<AssessmentWorkspace />} />
            <Route path="assessments/:id/actions" element={<ActionsPage />} />
            <Route path="deals" element={<DealsPage />} />
            <Route path="marketplace" element={<Marketplace />} />
            <Route path="cai" element={<CAIControlTower />} />
            <Route path="cai/projects/:id" element={<CAIProjectWorkspace />} />
            <Route path="cai/reporting" element={<UNFCCCReportingPage />} />
            <Route path="upgrade" element={<UpgradePage />} />
            <Route path="sme-journey" element={
              <WorkspaceGuard workspace="sme">
                <SMEJourneyPage />
              </WorkspaceGuard>
            } />
            <Route path="sme-reports" element={<SMEReports />} />
            <Route path="aggregation-enrollment" element={<AggregationEnrollment />} />
            <Route path="carbon-pathways" element={<CarbonPathwaysPage />} />
            <Route path="financing" element={<FinancingPage />} />
            <Route path="sme/journey" element={
              <WorkspaceGuard workspace="sme">
                <SMEJourneyWorkspace />
              </WorkspaceGuard>
            } />
            {/* SME Periodic Workspace — monthly tracking routes */}
            <Route path="sme/monthly/inputs"  element={<WorkspaceGuard workspace="sme"><SMEMonthlyInputs /></WorkspaceGuard>} />
            <Route path="sme/monthly/trends"  element={<WorkspaceGuard workspace="sme"><SMEEvolutionView /></WorkspaceGuard>} />
            <Route path="project/assessment" element={<ProjectAssessmentPage />} />
            <Route path="project/assessment/:id" element={<ProjectAssessmentPage />} />
            <Route path="housing-project" element={<CoopHousingWorkspace />} />
            <Route path="housing/climate-snapshot" element={<HousingClimateSnapshot />} />
            <Route path="housing/sdg" element={<HousingSDGPage />} />
            <Route path="housing/reports" element={<HousingReports />} />
            <Route path="housing/financing" element={<HousingFinancing />} />
            <Route path="housing/carbon-strategy" element={<HousingCarbonStrategy />} />
            <Route path="housing/carbon-structuring" element={<CarbonStructuringWorkspace />} />
            <Route path="housing/input-data" element={<HousingInputData />} />
            <Route path="housing/evidence-vault" element={<HousingEvidenceVault />} />
            <Route path="housing/training" element={<HousingTraining />} />
            {/* Generic multi-sector workspace routes — all 10 tabs, housing shell standard */}
            <Route path="project/:id/workspace"          element={<GenericProjectWorkspace />} />
            <Route path="project/:id/input-data"         element={<GenericInputData />} />
            <Route path="project/:id/climate-snapshot"   element={<GenericClimateSnapshot />} />
            <Route path="project/:id/sdg"                element={<GenericSDGContribution />} />
            <Route path="project/:id/carbon-strategy"    element={<GenericCarbonStrategy />} />
            <Route path="project/:id/carbon-structuring" element={<GenericCarbonStructuring />} />
            <Route path="project/:id/evidence-vault"     element={<GenericEvidenceVault />} />
            <Route path="project/:id/reports"            element={<GenericReports />} />
            <Route path="project/:id/financing"          element={<GenericFinancing />} />
            <Route path="project/:id/training"           element={<GenericTraining />} />
            {/* New project user onboarding — admin-created users with no project yet */}
            <Route path="project/start" element={<WorkspaceGuard workspace="project"><ProjectStart /></WorkspaceGuard>} />
            {/* Legacy safety route — redirects to /project/start */}
            <Route path="no-project-assigned" element={<Navigate to="/project/start" replace />} />
            <Route path="evidence" element={<EvidenceVault />} />
            <Route path="reports" element={<Reports />} />
            <Route path="passport/:id" element={<Passport />} />
            <Route path="app-registry" element={<Registry />} />
            <Route path="academy" element={<Academy />} />
            <Route path="methodologies" element={<Methodologies />} />
            <Route path="settings" element={<Settings />} />
            <Route path="fi" element={<Navigate to="/fi/workspace" replace />} />
            <Route path="fi/requests" element={<Navigate to="/fi/workspace" replace />} />
            <Route path="fi/workspace" element={
              <WorkspaceGuard workspace="fi"><FIWorkspace /></WorkspaceGuard>
            } />
            <Route path="fi/projects" element={<Navigate to="/fi/workspace" replace />} />
            <Route path="fi/financing-reviews" element={<Navigate to="/fi/workspace" replace />} />
            <Route path="fi/:id" element={<Navigate to="/fi/workspace" replace />} />
            <Route path="corporate" element={<CorporateDashboard />} />
            <Route path="corporate/structure" element={<CorporateStructure />} />
            <Route path="corporate/targets" element={<CorporateTargets />} />
            <Route path="corporate/initiatives" element={<CorporateInitiatives />} />
            <Route path="corporate/pipeline" element={<CorporatePipeline />} />
            <Route path="corporate/workspace" element={<CorporateWorkspace />} />
            <Route path="corporate/programs" element={<CorporateProgramBuilder />} />
            <Route path="corporate/monthly/inputs" element={<CorporateMonthlyInputs />} />
            <Route path="corporate/monthly/trends" element={<CorporateEvolutionView />} />
            <Route path="corporate/credentials" element={<CorporateCredentials />} />
            {/* NOTE: corporate/sdg, carbon-structuring, evidence-vault are handled by DangoteProvider nested routes below */}
            <Route path="corporate/monetisation" element={<CorporateCarbonMonetisation />} />
            <Route path="corporate/financing-readiness" element={<CorporateFinancingReadiness />} />
            <Route path="corporate/portfolio" element={<CorporateEvolutionView defaultView="portfolio" />} />
            {/* ── Dangote Corporate Platform — DangoteProvider wraps all /corporate/* routes ── */}
            <Route path="corporate/*" element={<DangoteProvider><Outlet /></DangoteProvider>}>
              <Route path="dashboard"          element={<DangoteDashboard />} />
              <Route path="bu/:bu_id"           element={<DangoteBusinessUnit />} />
              <Route path="reporting"           element={<DangoteMonthlyReport />} />
              <Route path="asset/:asset_id"     element={<DangoteAssetDetail />} />
              <Route path="carbon-strategy"     element={<DangoteCarbonStrategy />} />
              <Route path="value-bridge"        element={<DangoteCarbonValueBridge />} />
              <Route path="carbon-structuring"  element={<DangoteCarbonStructuring />} />
              <Route path="climate"             element={<DangoteClimateSnapshot />} />
              <Route path="sdg"                 element={<DangoteSDGContribution />} />
              <Route path="evidence-vault"      element={<DangoteEvidenceVault />} />
              <Route path="financing"           element={<DangoteFinancing />} />
              <Route path="reports"             element={<DangoteReports />} />
              <Route index element={<Navigate to="/corporate/dashboard" replace />} />
            </Route>

            {/* ── Nigeria Breweries Plc Workspace ────────────────────────────────── */}
            <Route path="corporate/nbplc" element={<NBPLCProvider><Outlet /></NBPLCProvider>}>
              <Route path="dashboard"          element={<NBPLCDashboard />} />
              <Route path="reporting"          element={<NBPLCMonthlyReport />} />
              <Route path="climate"            element={<NBPLCClimateSnapshot />} />
              <Route path="sdg"                element={<NBPLCSDGContribution />} />
              <Route path="value-bridge"       element={<NBPLCCarbonValueBridge />} />
              <Route path="carbon-strategy"    element={<NBPLCCarbonStrategy />} />
              <Route path="carbon-structuring" element={<NBPLCCarbonStructuring />} />
              <Route path="evidence-vault"     element={<NBPLCEvidenceVault />} />
              <Route path="financing"          element={<NBPLCFinancing />} />
              <Route path="reports"            element={<NBPLCReports />} />
              <Route index element={<Navigate to="/corporate/nbplc/dashboard" replace />} />
            </Route>

            {/* ── LCFPI routes moved to top-level standalone (see below Layout block) ── */}

            {/* ── MTN Nigeria Enterprise Workspace — 10 Pages ──────────────── */}
            <Route path="mtn" element={<WorkspaceGuard workspace="mtn"><MTNProvider><MTNLayout /></MTNProvider></WorkspaceGuard>}>
              <Route path="dashboard"     element={<MTNDashboard />} />
              <Route path="workspace"     element={<MTNWorkspace />} />
              <Route path="intelligence"  element={<MTNIntelligence />} />
              <Route path="sdg-impact"    element={<MTNSdgImpact />} />
              <Route path="finance"       element={<MTNFinance />} />
              <Route path="carbon-markets" element={<MTNCarbonMarkets />} />
              <Route path="pdd-readiness" element={<MTNPddReadiness />} />
              <Route path="esg-disclosure" element={<MTNEsgDisclosure />} />
              <Route path="evidence"      element={<MTNEvidenceVault />} />
              <Route path="settings"      element={<MTNSettings />} />
              <Route index element={<Navigate to="/mtn/dashboard" replace />} />
            </Route>

            {/* ── Nosak Group Enterprise Workspace — 7-Page Consolidated ── */}
            <Route path="nosak" element={<NOSAKProvider><NosAKLayout /></NOSAKProvider>}>
              <Route path="dashboard"    element={<NosAKDashboard />} />
              <Route path="workspace"    element={<NosAKDataWorkspace />} />
              <Route path="intelligence" element={<NosAKIntelligence />} />
              <Route path="finance"      element={<NosAKFinanceCarbon />} />
              <Route path="esg"          element={<NosAKESGRisk />} />
              <Route path="evidence"     element={<NosAKEvidenceVault />} />
              <Route path="settings"     element={<NosAKSettings />} />
              {/* Legacy redirects — all old routes point to consolidated pages */}
              <Route path="climate"        element={<Navigate to="/nosak/intelligence" replace />} />
              <Route path="sdg"            element={<Navigate to="/nosak/esg" replace />} />
              <Route path="manufacturing"  element={<Navigate to="/nosak/intelligence" replace />} />
              <Route path="agriculture"    element={<Navigate to="/nosak/intelligence" replace />} />
              <Route path="circularity"    element={<Navigate to="/nosak/intelligence" replace />} />
              <Route path="energy"         element={<Navigate to="/nosak/intelligence" replace />} />
              <Route path="risk"           element={<Navigate to="/nosak/esg" replace />} />
              <Route path="carbon-assets"  element={<Navigate to="/nosak/finance" replace />} />
              <Route path="scenario"       element={<Navigate to="/nosak/finance" replace />} />
              <Route path="esg-reporting"  element={<Navigate to="/nosak/esg" replace />} />
              <Route path="audit"          element={<Navigate to="/nosak/settings" replace />} />
              <Route index element={<Navigate to="/nosak/dashboard" replace />} />
            </Route>
            {/* ── Lagos State Climate Platform ─────────────────────────────────── */}
            <Route path="state/lagos" element={<LAGOSProvider><Outlet /></LAGOSProvider>}>
              <Route path="dashboard"          element={<LAGOSDashboard />} />
              <Route path="reporting"          element={<LAGOSEntityReporting />} />
              <Route path="entity/:entityId"   element={<LAGOSEntityProfile />} />
              <Route path="climate"            element={<LAGOSClimateCircularity />} />
              <Route path="sdg"                element={<LAGOSSDGContribution />} />
              <Route path="carbon-value"       element={<LAGOSCarbonValueBridge />} />
              <Route path="carbon-strategy"    element={<LAGOSCarbonStrategy />} />
              <Route path="carbon-structuring" element={<LAGOSCarbonStructuring />} />
              <Route path="financials"         element={<LAGOSFinancials />} />
              <Route path="climate-investment-structuring" element={<LAGOSClimateInvestmentStructuring />} />
              <Route path="evidence"           element={<LAGOSEvidenceVault />} />
              <Route path="reports"            element={<LAGOSReports />} />
              <Route path="intelligence"       element={<LAGOSStateIntelligence />} />
              <Route path="eco-labels"         element={<LAGOSEcoLabels />} />
              <Route path="settings"           element={<LAGOSSettings />} />
              <Route index element={<Navigate to="/state/lagos/dashboard" replace />} />
            </Route>
            <Route path="corporate/nirsal" element={<NIRSALProvider><Outlet /></NIRSALProvider>}>
              <Route path="dashboard"          element={<NIRSALDashboard />} />
              <Route path="reporting"          element={<NIRSALPortfolioReport />} />
              <Route path="climate"            element={<NIRSALClimateImpact />} />
              <Route path="sdg"                element={<NIRSALSDGContribution />} />
              <Route path="value-bridge"       element={<NIRSALCarbonValueBridge />} />
              <Route path="carbon-strategy"    element={<NIRSALCarbonStrategy />} />
              <Route path="carbon-structuring" element={<NIRSALCarbonStructuring />} />
              <Route path="evidence-vault"     element={<NIRSALEvidenceVault />} />
              <Route path="financials"         element={<NIRSALFinancials />} />
              <Route path="reports"            element={<NIRSALReports />} />
              <Route index element={<Navigate to="/corporate/nirsal/dashboard" replace />} />
            </Route>
            {/* SME new pages */}
            <Route path="sme/overview" element={<SMEOverview />} />
            <Route path="sme/credentials" element={<SMECredentials />} />
            <Route path="sme/climate-snapshot" element={<SMEClimateSnapshot />} />
            <Route path="sme/sdg" element={<SMESDGContribution />} />
            <Route path="sme/financing-readiness" element={<SMEFinancingReadiness />} />
            {/* FI new pages — all redirect to unified FI workspace */}
            <Route path="fi/eligibility" element={<Navigate to="/fi/workspace" replace />} />
            <Route path="fi/capital-deployment" element={<Navigate to="/fi/workspace" replace />} />
            {/* States workspace */}
            {/* ── States Workspace — V102 Dynamic State Resolution ─────────────── */}
            {/* Base /states → StateEntry resolves first available state dynamically (NO hardcoded ID) */}
            <Route path="states" element={<WorkspaceGuard workspace="states"><StateEntry /></WorkspaceGuard>} />
            {/* Backward compat: old unscoped routes → StateEntry (dynamic resolution) */}
            <Route path="states/overview"            element={<Navigate to="/states" replace />} />
            <Route path="states/climate-snapshot"    element={<Navigate to="/states" replace />} />
            <Route path="states/aggregated-projects" element={<Navigate to="/states" replace />} />
            <Route path="states/credentials"         element={<Navigate to="/states" replace />} />
            <Route path="states/sdg"                 element={<Navigate to="/states" replace />} />
            <Route path="states/carbon-pathways"     element={<Navigate to="/states" replace />} />
            <Route path="states/programmes"          element={<Navigate to="/states" replace />} />
            <Route path="states/labeling"            element={<Navigate to="/states" replace />} />
            <Route path="states/reports"             element={<Navigate to="/states" replace />} />
            <Route path="states/financing"           element={<Navigate to="/states" replace />} />
            {/* Scoped routes: /states/:stateId/... — StateLayout wraps all scoped pages */}
            <Route path="states/:stateId" element={<WorkspaceGuard workspace="states"><StateLayout /></WorkspaceGuard>}>
              <Route index element={<StateOverview />} />
              <Route path="overview"            element={<StateOverview />} />
              <Route path="climate-snapshot"    element={<StateClimateSnapshot />} />
              <Route path="aggregated-projects" element={<StateAggregatedProjects />} />
              <Route path="credentials"         element={<StateCredentials />} />
              <Route path="sdg"                 element={<StateSDG />} />
              <Route path="carbon-pathways"     element={<StateCarbonPathways />} />
              <Route path="programmes"          element={<StateProgrammes />} />
              <Route path="labeling"            element={<StateLabeling />} />
              <Route path="reports"             element={<StateReports />} />
              <Route path="financing"           element={<StateFinancing />} />
            </Route>
            <Route path="calculator" element={<NigeriaCalculator />} />
          </Route>

          <Route path="/analyst/reviews" element={<ProtectedRoute><AnalystWorkspace /></ProtectedRoute>} />
          {/* Admin Governance Workspace — 12 modules with own sidebar */}
          <Route path="/admin/*" element={<ProtectedRoute><AdminRoute><AdminWorkspace /></AdminRoute></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><AdminRoute><AdminWorkspace /></AdminRoute></ProtectedRoute>} />
          <Route path="/admin/verifiers" element={<ProtectedRoute><AdminRoute><AdminVerifiers /></AdminRoute></ProtectedRoute>} />
          <Route path="/admin/platform-integrity" element={<ProtectedRoute><AdminRoute><PlatformIntegrityDashboard /></AdminRoute></ProtectedRoute>} />
          {/* Verifier Portal — separate from main app, own auth */}
          <Route path="/verifier" element={<VerifierPortal />} />
          {/* LCFPI Public Landing — fully public, no auth required */}
          <Route path="/lcfpi" element={<LCFPILanding />} />
          {/* ── LCFPI — IC Mode IS the platform. One experience. ────────── */}
          {/* Default route → IC Mode */}
          <Route path="/lcfpi/dashboard" element={<WorkspaceGuard workspace="lcfpi"><LCFPIProvider><LCFPIInvestmentCommitteeMode /></LCFPIProvider></WorkspaceGuard>} />
          {/* ic-mode alias */}
          <Route path="/lcfpi/ic-mode" element={<Navigate to="/lcfpi/dashboard" replace />} />
          {/* ALL legacy routes → IC Mode (dashboard = the platform, no sidebar pages) */}
          <Route path="/lcfpi/projects"            element={<Navigate to="/lcfpi/dashboard" replace />} />
          <Route path="/lcfpi/governance"          element={<Navigate to="/lcfpi/dashboard" replace />} />
          <Route path="/lcfpi/pipeline"            element={<Navigate to="/lcfpi/dashboard" replace />} />
          <Route path="/lcfpi/finance"             element={<Navigate to="/lcfpi/dashboard" replace />} />
          <Route path="/lcfpi/impact"              element={<Navigate to="/lcfpi/dashboard" replace />} />
          <Route path="/lcfpi/submit"              element={<Navigate to="/lcfpi/dashboard" replace />} />
          <Route path="/lcfpi/assessment"          element={<Navigate to="/lcfpi/dashboard" replace />} />
          <Route path="/lcfpi/readiness"           element={<Navigate to="/lcfpi/dashboard" replace />} />
          <Route path="/lcfpi/eco-labels"          element={<Navigate to="/lcfpi/dashboard" replace />} />
          <Route path="/lcfpi/pdd-builder"         element={<Navigate to="/lcfpi/dashboard" replace />} />
          <Route path="/lcfpi/eoi-generator"       element={<Navigate to="/lcfpi/dashboard" replace />} />
          <Route path="/lcfpi/project-health"      element={<Navigate to="/lcfpi/dashboard" replace />} />
          <Route path="/lcfpi/workflow"            element={<Navigate to="/lcfpi/dashboard" replace />} />
          <Route path="/lcfpi/investment-profile"  element={<Navigate to="/lcfpi/dashboard" replace />} />
          <Route path="/lcfpi/investor-workspace"  element={<Navigate to="/lcfpi/dashboard" replace />} />
          <Route path="/lcfpi/dfi-matching"        element={<Navigate to="/lcfpi/dashboard" replace />} />
          <Route path="/lcfpi/portfolio"           element={<Navigate to="/lcfpi/dashboard" replace />} />
          <Route path="/lcfpi/eoi-tracker"         element={<Navigate to="/lcfpi/dashboard" replace />} />
          <Route path="/lcfpi/data-room"           element={<Navigate to="/lcfpi/dashboard" replace />} />
          <Route path="/lcfpi/monetisation"        element={<Navigate to="/lcfpi/dashboard" replace />} />
          <Route path="/lcfpi/poa"                 element={<Navigate to="/lcfpi/dashboard" replace />} />
          <Route path="/lcfpi/poa-deal-room"       element={<Navigate to="/lcfpi/dashboard" replace />} />
          <Route path="/lcfpi/gov-workspace"       element={<Navigate to="/lcfpi/dashboard" replace />} />
          <Route path="/lcfpi/audit-trail"         element={<Navigate to="/lcfpi/dashboard" replace />} />
          <Route path="/lcfpi/reports"             element={<Navigate to="/lcfpi/dashboard" replace />} />
          <Route path="/lcfpi/settings"            element={<Navigate to="/lcfpi/dashboard" replace />} />
          <Route path="/lcfpi/registry"            element={<Navigate to="/lcfpi/dashboard" replace />} />
          <Route path="/lcfpi/digest"              element={<Navigate to="/lcfpi/dashboard" replace />} />

          {/* ── DBN Green, Digital and Inclusive Finance Platform ──── */}
          <Route path="/institutions/dbn" element={<DBNLayout />}>
            <Route index element={<Navigate to="/institutions/dbn/overview" replace />} />
            <Route path="overview"                element={<DBNOverview />} />
            <Route path="eib-facility"            element={<DBNEIBFacility />} />
            <Route path="pipeline"                element={<DBNPipeline />} />
            <Route path="projects"                element={<DBNProjects />} />
            <Route path="eligibility-assessment"  element={<DBNEligibilityAssessment />} />
            <Route path="ta-manager"              element={<DBNTAManager />} />
            <Route path="risk-additionality"      element={<DBNRiskAdditionality />} />
            <Route path="monetisation"            element={<DBNMonetisation />} />
            <Route path="reporting"               element={<DBNReporting />} />
            <Route path="ai-copilot"              element={<DBNAICopilot />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" richColors />
        </DemoTourWrapper>
      </BrowserRouter>
    </AuthProvider>
  );
}

function DemoTourWrapper({ children }) {
  const [demoActive, setDemoActive] = React.useState(false);
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("demo") === "true") setDemoActive(true);
  }, []);
  return (
    <>
      {children}
      {demoActive && <DemoTour onClose={() => setDemoActive(false)} />}
    </>
  );
}

function App() {
  return <AppRoutes />;
}

export default App;
